"use client";

// Extracted client component from page.tsx to allow server-side metadata
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import LocationModal from '@/components/common/LocationModal';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { readLocationCookie, writeLocationCookie } from '@/lib/location';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import PostCategoryPicker from '@/components/posts/PostCategoryPicker';
import MetroFeed from '@/components/posts/MetroFeed';
import ProfileJoinCta from '@/components/profile/ProfileJoinCta';
import type { PostCardProps } from '@/components/PostCard';
import { cn } from '@/lib/utils';
import { Flame, Briefcase, Megaphone, Sparkles, MapPin, Plus, ChevronRight, ChevronDown } from 'lucide-react';
import { API_BASE as API_BASE_RAW } from '@/lib/apiBase';
import { POST_SECTIONS, categoriesInSection, type PostSection } from '@/lib/posts/sections';
import { pinSection, readArrivalSection } from '@/lib/posts/pinSection';

interface Category {
  slug: string;
  name: string;
  type: 'connect' | 'hiring' | 'looking' | 'services' | string;
}

const API_BASE = API_BASE_RAW.replace(/\/+$/, '');

// Per-section icon and tint. The labels, subtitles and anchor ids come from
// the shared module; only the visual treatment is this page's business.
const SECTION_CHROME: Record<PostSection, { Icon: typeof Flame; iconColor: string; iconBg: string }> = {
  connect:  { Icon: Flame,     iconColor: 'text-pink-500',    iconBg: 'bg-pink-500/10' },
  hiring:   { Icon: Briefcase, iconColor: 'text-amber-500',   iconBg: 'bg-amber-500/10' },
  looking:  { Icon: Megaphone, iconColor: 'text-emerald-500', iconBg: 'bg-emerald-500/10' },
  services: { Icon: Sparkles,  iconColor: 'text-violet-400',  iconBg: 'bg-violet-500/10' },
};

// "Collaborate, Now hiring, Available for work, and Services" - built from the
// shared list so adding a section never leaves this sentence out of date.
const SECTION_NAMES = (() => {
  const labels = POST_SECTIONS.map(s => s.label);
  return labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1];
})();

interface NearbyMetro { slug: string; label: string }
interface MetroFaq { q: string; a: string }

export default function ClientPage({
  metroState,
  initialCategories = [],
  editorialIntro = null,
  editorialBody = null,
  editorialFaq = null,
  talentCount = 0,
  nearbyMetros = [],
  initialFeed = null,
  initialFeatured = null,
  isGuest = false,
}: {
  metroState?: string | null;
  initialCategories?: Category[];
  editorialIntro?: string | null;
  editorialBody?: string[] | null;
  editorialFaq?: MetroFaq[] | null;
  talentCount?: number;
  nearbyMetros?: NearbyMetro[];
  /** SSR'd viewer-correct metro feed; null = server fetch failed. */
  initialFeed?: PostCardProps[] | null;
  /** SSR'd featured casting calls (B3); null = the fetch failed, which renders nothing. */
  initialFeatured?: PostCardProps[] | null;
  isGuest?: boolean;
}) {
  const { location } = useParams() as { location: string };
  const router = useRouter();
  const [cats, setCats] = useState<Category[]>(initialCategories);
  const [locOpen, setLocOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const catDetailsRef = useRef<HTMLDetailsElement>(null);

  // Which section the visitor is arriving for, resolved once per mount. Kept in
  // a ref so a StrictMode re-run of the effect in development cannot lose a
  // relayed bucket that the first run already consumed from sessionStorage.
  const arrivalRef = useRef<string | null | undefined>(undefined);

  // Reveal and land on a category section when arriving from a /jobs-hub bucket
  // CTA (charter-6 Phase 0). The #type-* section cards live INSIDE the folded
  // <details> below, so a bare URL hash cannot reach them: Next's soft-nav
  // scroll skips zero-rect nodes and never expands a <details>. The bucket
  // comes from the URL hash (direct links) or from the sessionStorage relay set
  // by the /posts chooser, which survives the LocationModal detour and a
  // redirect that drops the hash.
  //
  // Why this is a layout effect and a hold rather than one timed scroll. A
  // single scroll 80 ms after mount was a race it lost most of the time: on a
  // client-side arrival Next has already scrolled the loading skeleton into
  // view and spent its one-shot scroll intent before this page exists, and
  // content above the cards keeps growing for a second or more after mount
  // (thin-metro fill, category re-fetch, admin and email banners), so whatever
  // landed after the smooth scroll pushed the section back out of view. A
  // layout effect runs before Next's own scroll handler in the same commit and
  // before paint, so when no skeleton was shown the fold is already open when
  // Next looks for the hash target and it lands the section itself; when the
  // skeleton was shown, pinSection lands it before the first paint of the real
  // page. pinSection then re-measures the card every frame for a bounded
  // window, re-scrolls instantly whenever it drifts, stops on the first wheel,
  // touch, key or pointer, and cleans up on unmount. It never calls the
  // router and never writes the URL or history.
  useLayoutEffect(() => {
    if (arrivalRef.current === undefined) {
      let storage: Storage | null = null;
      try { storage = window.sessionStorage; } catch {}
      arrivalRef.current = readArrivalSection(window.location.hash, storage);
    }
    const sectionId = arrivalRef.current;
    if (!sectionId) return;
    if (catDetailsRef.current) catDetailsRef.current.open = true;
    return pinSection(sectionId);
    // Mount-only: the bucket intent is consumed once on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist { state, metro } cookie. Categories come from SSR via initialCategories;
  // only re-fetch on the client if SSR returned empty (network blip on the server).
  useEffect(() => {
    let state = metroState || '';
    if (!state) {
      try {
        const loc = readLocationCookie();
        if (loc?.state) state = loc.state;
      } catch {}
    }

    writeLocationCookie({ state, metro: location });

    if (initialCategories && initialCategories.length > 0) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/categories?metro=${encodeURIComponent(location)}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`Categories fetch failed (${res.status})`);
        const data = await res.json();
        if (!cancelled) setCats(data);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          try { console.warn('Categories fetch failed:', (e as any)?.message || e); } catch {}
        }
        if (!cancelled) setCats([]);
      }
    })();

    return () => { cancelled = true };
  }, [location]);

  // Format "phoenix-market" -> "Phoenix Market"
  const placeName = location
    .replace(/-/g, ' ')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  // Custom sort for "connect" categories
  const CONNECT_ORDER = ['m4m','m4w','w4m','w4w','m4c','w4c','c4c'] as const;
  const ORDER_MAP: Record<string, number> = Object.fromEntries(
    CONNECT_ORDER.map((s, i) => [s, i])
  );
  const HIRING_PRIORITIES: Array<{ slug: string; label: string }> = [
    { slug: 'male-female-scenes', label: 'Male Female Scenes' },
    { slug: 'male-male-scenes', label: 'Male Male Scenes' },
    { slug: 'female-female-scenes', label: 'Female Female Scenes' },
    { slug: 'fetish-scenes', label: 'Fetish Scenes' },
  ];
  const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  // Ordering only. Which categories land in which section is decided by the
  // shared rule in lib/posts/sections.ts, so this page and the create picker
  // cannot disagree about a category. Before 2026-09-01 both files carried
  // their own copy and both filed `available-*` under "Now hiring", which is
  // a performer advertising herself shown as a producer offering paid work.
  const byType = (section: PostSection) => {
    const inSection = categoriesInSection(cats, section);

    if (section === 'connect') {
      return inSection.sort((a, b) => {
        const ai = ORDER_MAP[(a.slug || '').toLowerCase()] ?? Number.POSITIVE_INFINITY;
        const bi = ORDER_MAP[(b.slug || '').toLowerCase()] ?? Number.POSITIVE_INFINITY;
        if (ai !== bi) return ai - bi;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    }

    if (section === 'hiring') {
      const priorityIndex: Record<string, number> = Object.fromEntries(
        HIRING_PRIORITIES.map((x, i) => [x.slug, i])
      );
      const labelToSlug: Record<string, string> = Object.fromEntries(
        HIRING_PRIORITIES.map((x) => [slugify(x.label), x.slug])
      );
      return inSection
        .map((c, i) => {
          const slugLc = (c.slug || '').toLowerCase();
          const nameSlug = slugify(c.name || '');
          const matchedSlug = priorityIndex.hasOwnProperty(slugLc)
            ? slugLc
            : (labelToSlug[nameSlug] || '');
          const p = matchedSlug ? priorityIndex[matchedSlug] : Number.POSITIVE_INFINITY;
          const normalizedName = matchedSlug
            ? (HIRING_PRIORITIES.find(x => x.slug === matchedSlug)?.label || c.name)
            : c.name;
          return { c: { ...c, name: normalizedName }, p, i };
        })
        .sort((a, b) => (a.p - b.p) || (a.i - b.i))
        .map(x => x.c);
    }

    // looking and services: plain alphabetical.
    return inSection.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
      {/* Breadcrumb */}
      <nav className="mb-3 text-sm text-zinc-500 dark:text-zinc-400" aria-label="Breadcrumb">
        <Link href="/posts" className="hover:underline">Posts</Link>
        <span className="mx-2 text-zinc-300 dark:text-zinc-700">/</span>
        <span className="font-semibold text-zinc-900 dark:text-zinc-100">{placeName}</span>
      </nav>

      {/* Hero card */}
      <section className="relative mb-6 overflow-hidden rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-fuchsia-500/10 blur-3xl"
        />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h1 className="font-serif text-[clamp(2.25rem,7vw,4rem)] font-semibold leading-[1.05] tracking-tight text-zinc-900 dark:text-zinc-50">
              Porn jobs and casting calls in {placeName}
            </h1>
            <p className="mt-3 max-w-lg text-balance text-base text-zinc-600 dark:text-zinc-300 sm:text-lg">
              Casting calls, collaborations, crew, and creator services in {placeName}.
            </p>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => setLocOpen(true)}
            >
              <MapPin className="h-4 w-4" aria-hidden />
              <span>Change location</span>
            </Button>
            <Button
              type="button"
              variant="primary"
              className="gap-2"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="h-4 w-4" aria-hidden />
              <span>Create a post</span>
            </Button>
          </div>
        </div>
      </section>

      {/* Unified metro feed (shape brief 2026-07-11): the hub opens on actual
          posts, not a directory of mostly-empty category links. */}
      <MetroFeed location={location} placeName={placeName} initialFeed={initialFeed} featured={initialFeatured} />

      {/* Guest ask sits between the feed and the reference material. */}
      {isGuest && (
        <div className="mb-8">
          <ProfileJoinCta
            returnTo={`/posts/${location}`}
            heading={`Post and get booked in ${placeName}`}
            body={`JuicyStarz is where adult-industry talent, producers, and crew connect. Join free to message ${placeName} members, post casting calls, and appear in local search.`}
          />
        </div>
      )}

      {/* Category directory, folded but SSR'd: <details> keeps all category
          links in the served HTML (crawl equity to category landing pages)
          while the feed above is the primary surface. */}
      <details ref={catDetailsRef} className="group rounded-3xl border border-zinc-200/60 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-6 sm:p-8 [&::-webkit-details-marker]:hidden">
          <div>
            <h2 className="font-serif text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              Browse all categories
            </h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {cats.length > 0 ? `${cats.length} categories across ${SECTION_NAMES}` : SECTION_NAMES}
            </p>
          </div>
          <ChevronDown className="h-5 w-5 shrink-0 text-zinc-400 transition-transform group-open:rotate-180" aria-hidden />
        </summary>
        <div className="px-6 pb-6 sm:px-8 sm:pb-8">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {POST_SECTIONS.map(sec => {
          const list = byType(sec.key);
          const { Icon, iconColor, iconBg } = SECTION_CHROME[sec.key];
          return (
            <Card
              key={sec.key}
              id={`type-${sec.anchor}`}
              className="scroll-mt-20 rounded-3xl border-zinc-200/60 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950"
            >
              <CardHeader className="flex flex-row items-start gap-3 pb-4">
                <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', iconBg)}>
                  <Icon className={cn('h-5 w-5', iconColor)} aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle className="font-serif text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
                    {sec.label}
                  </CardTitle>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{sec.subtitle}</p>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {list.length > 0 ? (
                  <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
                    {list.map(cat => (
                      <li key={cat.slug}>
                        <Link
                          href={`/posts/${location}/${cat.slug}`}
                          className="group flex items-center justify-between py-3"
                        >
                          <span className="font-medium text-zinc-700 transition-colors group-hover:text-zinc-950 dark:text-zinc-200 dark:group-hover:text-zinc-50">
                            {cat.name}
                          </span>
                          <ChevronRight className="h-4 w-4 text-zinc-400 transition-transform group-hover:translate-x-0.5 dark:text-zinc-500" aria-hidden />
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-zinc-500 dark:text-zinc-400">No categories available.</div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
        </div>
      </details>

      {/* Local market context: durable, crawlable content + nearby-metro
          internal links. Present on every metro hub; for curated metros it also
          carries the unique editorial (intro + body + FAQ) that makes the page
          a genuinely useful, standalone page and gets it indexed. */}
      {(editorialIntro || (editorialBody && editorialBody.length > 0) || talentCount > 0 || nearbyMetros.length > 0) && (
        <section className="mt-8 rounded-3xl border border-zinc-200/60 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-8">
          <h2 className="font-serif text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Adult industry work in {placeName}
          </h2>
          <div className="mt-3 space-y-3 text-zinc-600 dark:text-zinc-300">
            {editorialIntro && <p>{editorialIntro}</p>}
            {editorialBody?.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
            <p>
              {talentCount > 0
                ? `${talentCount.toLocaleString()} members in the ${placeName} area. `
                : ''}
              On JuicyStarz, {placeName} members connect for casting calls, collaborations, paid adult work, and creator services. Browse{' '}
              <Link href="/posts" className="font-medium text-pink-600 underline-offset-2 hover:underline dark:text-pink-400">
                porn jobs by city
              </Link>{' '}
              across the U.S., or pick a category above to get started in {placeName}.
            </p>
          </div>
          {editorialFaq && editorialFaq.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Frequently asked questions
              </h3>
              <dl className="mt-3 space-y-4">
                {editorialFaq.map((f, i) => (
                  <div key={i}>
                    <dt className="font-semibold text-zinc-900 dark:text-zinc-100">{f.q}</dt>
                    <dd className="mt-1 text-zinc-600 dark:text-zinc-300">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {nearbyMetros.length > 0 && (
            <div className="mt-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Nearby markets
              </h3>
              <ul className="mt-2 flex flex-wrap gap-2">
                {nearbyMetros.map(m => (
                  <li key={m.slug}>
                    <Link
                      href={`/posts/${m.slug}`}
                      className="inline-flex rounded-full border border-zinc-200 px-3 py-1 text-sm text-zinc-700 transition-colors hover:border-zinc-300 hover:text-zinc-950 dark:border-zinc-800 dark:text-zinc-200 dark:hover:text-zinc-50"
                    >
                      {m.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <LocationModal open={locOpen} onClose={() => setLocOpen(false)} onChanged={(_state, metro) => router.push(`/posts/${metro}`)} />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Create a post</DialogTitle>
            <DialogDescription>Pick a section and category for {placeName}.</DialogDescription>
          </DialogHeader>
          <PostCategoryPicker
            initialLocation={location}
            onCancel={() => setCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
